import { Box } from '@jfs/ui-new'
import { FC, ReactElement, useEffect, useState } from 'react'
import { FlatList } from 'react-native'
import { TableItem } from '../../comps/TableItem'
import { tableItemStrings } from '../../data/static/strings'
import { dateInUTC, localStringToNumber } from '../../util/util'
import { ListItem } from '../ListItem'
import { ListItemType, ListTypes } from '../ListItem/types'
import styles from './styles'
import { COMPONENT, ListData, ListProps } from './types'

const handleSort = (
  sortKey: keyof ListData,
  data: ListData[],
  sortOrder: string,
): { data: ListData[]; order: string; sortKey: string | number } => {
  const dataTosort = data.slice(1)
  let order: string = tableItemStrings?.noSortOrder
  const sortData = dataTosort.sort((a: ListData, b: ListData) => {
    let comparison = 1
    let x = a[sortKey]
    let y = b[sortKey]
    if (sortKey === 'totalAmount') {
      // for numbers
      x = localStringToNumber(String(a[sortKey]))
      y = localStringToNumber(String(b[sortKey]))
      return sortOrder === 'desc' ? x - y : y - x
    }
    if (sortKey === 'date') {
      // converting date from dd/mm to mm/dd
      x = Date.parse(dateInUTC(x)) ?? x
      y = Date.parse(dateInUTC(y)) ?? y
    }
    comparison = x > y ? 1 : -1
    return sortOrder === 'desc' ? comparison : comparison * -1
  })
  order = sortOrder === 'asc' ? 'desc' : 'asc'
  const sortedData = data.slice(0, 1).concat(sortData)
  return { data: sortedData, order: order, sortKey }
}

const getItemType: any = {
  DEFAULT: ListTypes.DEFAULT,
  SIDE_MENU: ListTypes.SIDE_MENU,
  MENU_TITLE: ListTypes.MENU_TITLE,
  NOTIFICATION: ListTypes.NOTIFICATION,
  Modes: ListTypes.MODES,
  SEARCH: ListTypes.SEARCH,
  EXPORT: ListTypes.EXPORT,
  CALENDAR: ListTypes.CALENDAR,
  SEARCH_FILTERS: ListTypes.SEARCH_FILTERS,
  INVOICE: ListTypes.INVOICE,
}

const getComponentType: any = {
  DEFAULT: COMPONENT.LIST,
  SIDE_MENU: COMPONENT.LIST,
  MENU_TITLE: COMPONENT.LIST,
  NOTIFICATION: COMPONENT.LIST,
  Modes: COMPONENT.LIST,
  SEARCH: COMPONENT.LIST,
  EXPORT: COMPONENT.LIST,
  CALENDAR: COMPONENT.LIST,
  Block: COMPONENT.TABLE,
  TABLE: COMPONENT.TABLE,
  SEARCH_FILTERS: COMPONENT.LIST,
  INVOICE: COMPONENT.LIST,
}

const List: FC<ListProps> = ({
  type = ListItemType.TABLE,
  data,
  onPress,
  header,
  footer,
  emptyComponent,
  selectedPos = 0,
  transactionType: trxType = 0,
  hide = false,
  selectedItems,
  onItemAction,
  onChangeInput,
  extendedColumns,
  disableItem = true,
  from = '',
  freezeColumn = '',
  onDownloadFileAction,
  onDownloadReportFile,
  onDeleteReportFile,
  isStickyHeader = false,
  showHideSubMenu,
  onEditReport,
  onLayout,
  onContentSizeChange,
  flatListRef,
  onScroll,
}: ListProps) => {
  const [lData, setLData] = useState(data)
  const [sortDetails, setSortDetails] = useState({
    key: tableItemStrings?.defaultSortKey,
    order: tableItemStrings?.decreasingSortOrder,
  })
  const handleSortFn = (sortKey: string) => {
    const sortedData = handleSort(sortKey, lData, sortDetails.order)
    setLData(sortedData.data.slice())
    setSortDetails({ key: sortedData.sortKey.toString(), order: sortedData.order })
  }

  useEffect(() => {
    if (data) {
      setLData(data)
    }
  }, [data])

  if (hide) {
    return null
  }

  const isSelected = (item: ListData): boolean => {
    if (selectedPos > -1) {
      return selectedPos === +item.id
    }
    return false
  }

  const getComponentFn = (
    componentType: COMPONENT,
    item: ListData,
    handleOnPress: () => void,
    isFreeze: boolean,
    itemType: any,
    handleShowHideSubMenu?: (showHide: boolean, parentId: string) => void,
  ): JSX.Element => {
    const getComponent = {
      LIST: (
        <ListItem
          type={itemType}
          data={item}
          selected={!!isSelected(item)}
          onPress={handleOnPress}
          showHideSubMenu={handleShowHideSubMenu}
        />
      ),
      TABLE: (
        <TableItem
          data={item}
          handleSort={handleSortFn}
          transactionType={trxType}
          extendedColumns={extendedColumns}
          disableItem={disableItem}
          sortDetails={sortDetails}
          from={from}
          freezeColumn={freezeColumn}
          isFreeze={isFreeze}
          onPress={handleOnPress}
          onAction={onItemAction}
          onDownloadFileAction={onDownloadFileAction}
          onChangeInput={onChangeInput}
          onDownloadReportFile={onDownloadReportFile}
          onDeleteReportFile={onDeleteReportFile}
          onEditReport={onEditReport}
        />
      ),
    }

    return getComponent[componentType] ?? null
  }

  const GetListItem = ({ item, index }: { item: ListData; index: number }): ReactElement => {
    const handleOnPress = (): void => {
      if (onPress) {
        onPress(item, index)
      }
    }
    const handleShowHideSubMenu = (showHide: boolean, parentId: string) => {
      showHideSubMenu && showHideSubMenu(showHide, parentId)
    }
    const itemType = getItemType[type]
    return getComponentFn(
      getComponentType[itemType] ?? COMPONENT.TABLE,
      item,
      handleOnPress,
      false,
      itemType,
      handleShowHideSubMenu,
    )
  }

  const GetFreezeListItem = ({ item, index }: { item: ListData; index: number }): ReactElement => {
    const handleOnPress = (): void => {
      if (onPress) {
        onPress(item, index)
      }
    }
    const itemType = getItemType[type]
    return getComponentFn(getComponentType[itemType] ?? COMPONENT.TABLE, item, handleOnPress, true, itemType)
  }

  const getFreezeData = () => {
    let fdata: any[] = []
    lData.forEach((item: any) => {
      if (item.hasOwnProperty(freezeColumn)) {
        let tempOb = {
          id: `${item.id}`,
          [freezeColumn]: item[freezeColumn || ''],
        }
        fdata.push(tempOb)
      }
    })
    return fdata
  }

  const freezeData = getFreezeData()

  return (
    <>
      {freezeColumn?.length > 0 && (
        <Box {...styles.freezeList}>
          <FlatList
            overScrollMode="auto"
            data={freezeData}
            renderItem={GetFreezeListItem}
            keyExtractor={(item, index): string => item?.id?.toString() + index}
            extraData={selectedItems}
            ListHeaderComponent={header}
            ListFooterComponent={footer}
            nestedScrollEnabled
            ListEmptyComponent={emptyComponent}
            showsVerticalScrollIndicator={false}
            stickyHeaderIndices={isStickyHeader ? [0] : undefined}
          />
        </Box>
      )}
      <FlatList
        overScrollMode="auto"
        data={lData}
        renderItem={GetListItem}
        keyExtractor={(item, index): string => item?.id?.toString() + index}
        extraData={selectedItems}
        ListHeaderComponent={header}
        ListFooterComponent={footer}
        nestedScrollEnabled
        onLayout={onLayout}
        onContentSizeChange={onContentSizeChange}
        ListEmptyComponent={emptyComponent}
        showsVerticalScrollIndicator={false}
        stickyHeaderIndices={isStickyHeader ? [0] : undefined}
        ref={flatListRef}
        onScroll={onScroll}
      />
    </>
  )
}

export default List
